import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import {
	Box,
	CircularProgress,
	Divider,
	FormControlLabel,
	IconButton,
	InputAdornment,
	Paper,
	Stack,
	Switch,
	TextField,
	Tooltip,
} from "@mui/material";
import { ContactType } from "@repo/server-api";
import { type IdWithName } from "@repo/shared/common";
import { MonthSlider, PureAsyncSelect, useAsyncSelectContactQuery } from "@repo/shared/components";
import { useTranslation } from "next-i18next";

import type { Dayjs } from "dayjs";

export type MonthlyOverviewToolbarProps = {
	selectedMonth: Dayjs;
	onMonthChange: (month: Dayjs | null) => void;
	selectedClient: IdWithName | null;
	onClientChange: (client: IdWithName | null) => void;
	selectedEmployee?: IdWithName | null;
	onEmployeeChange?: (employee: IdWithName | null) => void;
	searchValue: string;
	onSearchChange: (value: string) => void;
	excludeEmptyTenders: boolean;
	onExcludeEmptyTendersChange: (exclude: boolean) => void;
	excludeFullyInvoiced?: boolean;
	onExcludeFullyInvoicedChange?: (exclude: boolean) => void;
	groupByClient: boolean;
	onGroupByClientChange: (group: boolean) => void;
	onRefreshStatuses?: () => void;
	isRefreshing?: boolean;
};

export const MonthlyOverviewToolbar = ({
	selectedMonth,
	onMonthChange,
	selectedClient,
	onClientChange,
	selectedEmployee,
	onEmployeeChange,
	searchValue,
	onSearchChange,
	excludeEmptyTenders,
	onExcludeEmptyTendersChange,
	excludeFullyInvoiced,
	onExcludeFullyInvoicedChange,
	groupByClient,
	onGroupByClientChange,
	onRefreshStatuses,
	isRefreshing,
}: MonthlyOverviewToolbarProps) => {
	const { t } = useTranslation();

	const clientSearchHook = useAsyncSelectContactQuery([ContactType.Client]);
	const employeeSearchHook = useAsyncSelectContactQuery([
		ContactType.Employee,
		ContactType.Freelancer,
	]);

	return (
		<Paper
			elevation={0}
			sx={{
				mb: 2,
				p: 2,
				border: 1,
				borderColor: "divider",
				borderRadius: 2,
				flexShrink: 0,
				position: "sticky",
				top: 0,
				zIndex: 10,
				backgroundColor: "background.paper",
			}}>
			{/* Row 1: Main filters */}
			<Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
				<MonthSlider
					value={{
						year: selectedMonth.year(),
						month: selectedMonth.month() + 1,
					}}
					onChange={(monthValue) => {
						const newMonth = selectedMonth.year(monthValue.year).month(monthValue.month - 1);
						onMonthChange(newMonth);
					}}
				/>

				<PureAsyncSelect
					value={selectedClient}
					onChangeValue={onClientChange}
					label={t("client")}
					searchHook={clientSearchHook}
					additionalProps={{
						sx: { minWidth: 220, flex: 1, maxWidth: 300 },
						noOptionsText: t("no-clients-found"),
						loadingText: t("loading"),
						"data-testid": "client-autocomplete",
					}}
				/>

				{onEmployeeChange && (
					<PureAsyncSelect
						value={selectedEmployee ?? null}
						onChangeValue={onEmployeeChange}
						label={t("employee")}
						searchHook={employeeSearchHook}
						additionalProps={{
							sx: { minWidth: 220, flex: 1, maxWidth: 300 },
							noOptionsText: t("no-employees-found"),
							loadingText: t("loading"),
							"data-testid": "employee-autocomplete",
						}}
					/>
				)}

				<TextField
					label={t("search-project")}
					value={searchValue}
					onChange={(e) => onSearchChange(e.target.value)}
					placeholder={t("placeholder.search-project")}
					variant="outlined"
					sx={{ minWidth: 200, flex: 1, maxWidth: 300 }}
					slotProps={{
						input: {
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon fontSize="small" color="action" />
								</InputAdornment>
							),
						},
					}}
					data-testid="project-search"
				/>
			</Stack>

			<Divider sx={{ my: 1.5 }} />

			{/* Row 2: Toggle options + refresh */}
			<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
				<FormControlLabel
					control={
						<Switch
							size="small"
							checked={excludeEmptyTenders}
							onChange={(e) => onExcludeEmptyTendersChange(e.target.checked)}
							data-testid="exclude-empty-tenders-toggle"
						/>
					}
					label={t("exclude-empty-framework-contracts")}
				/>

				{onExcludeFullyInvoicedChange && (
					<FormControlLabel
						control={
							<Switch
								size="small"
								checked={excludeFullyInvoiced ?? false}
								onChange={(e) => onExcludeFullyInvoicedChange(e.target.checked)}
								data-testid="exclude-fully-invoiced-toggle"
							/>
						}
						label={t("exclude-fully-invoiced")}
					/>
				)}

				<FormControlLabel
					control={
						<Switch
							size="small"
							checked={groupByClient}
							onChange={(e) => onGroupByClientChange(e.target.checked)}
							data-testid="group-by-client-toggle"
						/>
					}
					label={t("group-by-client")}
				/>

				<Box sx={{ flex: 1 }} />

				{onRefreshStatuses && (
					<Tooltip title={t("refresh-invoice-statuses")}>
						<span>
							<IconButton
								onClick={onRefreshStatuses}
								disabled={isRefreshing}
								data-testid="refresh-invoice-statuses-button"
								color="primary"
								size="small">
								{isRefreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
							</IconButton>
						</span>
					</Tooltip>
				)}
			</Stack>
		</Paper>
	);
};
